import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { captureError } from "./utils/sentry";
import Register from "./components/auth/Register";
import VerifyEmail from "./components/auth/VerifyEmail";
import ResetPassword from "./components/auth/ResetPassword";
import AuthUnified from "./components/auth/AuthUnified";
import Dashboard from "./components/user/Dashboard";
import Profile from "./components/user/Profile";
import ForgetPassword from "./components/auth/ForgetPassword";
import ReactivateAccount from "./components/auth/ReactivateAccount";
import ClaimProfile from "./components/auth/ClaimProfile";
import SportsManagement from "./components/sports/SportsManagement";
import SportsAdminManagement from "./components/sports/SportsAdminManagement";
import SportsAdminCreate from "./components/sports/SportsAdminCreate";
import SportsAdminEdit from "./components/sports/SportsAdminEdit";
import OrganizationSportsManagement from "./components/sports/OrganizationSportsManagement";
import TournamentCreation from "./components/tournament/TournamentCreation";
import TournamentList from "./components/tournament/TournamentList";
import TournamentDetails from "./components/tournament/TournamentDetails";
import TournamentEdit from "./components/tournament/TournamentEdit";
import Leagues from "./components/league/Leagues";
import LeagueTeamPage from "./components/league/LeagueTeamPage";
import MatchList from "./components/match/MatchList";
import LiveMatch from "./components/match/live/LiveMatch";
import MatchDetails from "./components/match/MatchDetails";
import MatchCreation from "./components/match/MatchCreation";
import MatchManagement from "./components/match/MatchManagement";
import MatchEdit from "./components/match/MatchEdit";
import BadmintonScoreTracker from "./components/match/scoreboards/badminton/BadmintonScoreTracker";
import TeamsBridge from "./components/teams/TeamsBridge";
import DropInList from "./components/dropIn/DropInList";
import DropInCreation from "./components/dropIn/DropInCreation";
import DropInDetails from "./components/dropIn/DropInDetails";
import DropInEdit from "./components/dropIn/DropInEdit";
import PlayerProfile from "./components/user/PlayerProfile";
import FollowList from "./components/user/FollowList";
import DirectMessages from "./components/messages/DirectMessages";
import StatsPage from "./pages/StatsPage";
import PaymentsPage from "./components/payments/PaymentsPage";
import SchedulePage from "./components/user/SchedulePage";
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import FeedbackButton from "./components/feedback/FeedbackButton";
import QuickScore from "./components/quickscore/QuickScore";
import LeagueScreen from "./components/screen/LeagueScreen";
import { isTvScreenEnabled } from "./config/features";
import RequireAccess from "./components/auth/RequireAccess";
import { canCreateCompetition, canUseSportsAdmin } from "./utils/rbac";

import { WebSocketProvider } from "./contexts/WebSocketContext";
import { AuthProvider } from "./contexts/AuthContext";
import { UserFiltersProvider } from "./contexts/UserFiltersContext";
import { debugLog } from "./utils/debugLog";
import "./styles/base/globals.css";

/**
 * Error Boundary: catches any unhandled render error in the tree and shows a
 * user-friendly message instead of a blank white screen.  The error is also
 * logged to the console, and sent to Sentry when REACT_APP_SENTRY_DSN is set.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
    captureError(error, { componentStack: info?.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "sans-serif",
            background: "#0f1729",
            color: "#fff",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#ff6b6b", marginBottom: "1rem" }}>
            Something went wrong
          </h2>
          <p style={{ color: "#aaa", marginBottom: "1.5rem" }}>
            {this.state.error?.message || "An unexpected error occurred."}
          </p>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.href = "/login";
            }}
            style={{
              padding: "0.7rem 1.6rem",
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            Go to Login
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const { isAuthenticated, isLoading, token, logout, user: authUser } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();

  // Always use the live user object from AuthContext so role/permission changes
  // (e.g. after a background profile refresh) are reflected without re-login.
  // Fall back to localStorage only as a last resort to avoid passing null on
  // the very first render before the context has hydrated.
  const user = authUser || JSON.parse(localStorage.getItem("user") || "null");
  
  debugLog('🔄 App render:', { isAuthenticated, isLoading });

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // âœ… Show loading while checking authentication
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0f1729",
          color: "#fff",
          fontSize: "1.1rem",
          fontFamily: "sans-serif",
        }}
      >
        Loading Sportifide…
      </div>
    );
  }

  return (
    <div>
      <Routes>
        {/* Always-public routes — accessible regardless of auth state */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/quick-score" element={<QuickScore />} />
        {/* Venue screen (TV): off unless REACT_APP_FEATURE_TV_SCREEN=true; signed in or out,
            a private league checks access on the server */}
        {isTvScreenEnabled() && <Route path="/screen/league/:id" element={<LeagueScreen />} />}
        {/* Claim-your-profile link: works signed in (claim into this account) or out */}
        <Route path="/claim/:token" element={<ClaimProfile />} />

        {!isAuthenticated ? (
          <>
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/login" element={<AuthUnified />} />
            <Route path="/forgot-password" element={<ForgetPassword />} />
            <Route path="/reactivate-account" element={<ReactivateAccount />} />
            <Route path="*" element={<RedirectToLogin />} />
          </>
        ) : (
          <>
            <Route
              path="/dashboard"
              element={<Dashboard token={token} onLogout={handleLogout} />}
            />
            <Route
              path="/profile"
              element={<Profile token={token} onLogout={handleLogout} />}
            />
            <Route
              path="/sports"
              element={
                <SportsManagement
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/sports-admin"
              element={
                <RequireAccess allowed={canUseSportsAdmin(user)} what="manage the sports catalog">
                  <SportsAdminManagement
                    token={token}
                    user={user}
                    onLogout={handleLogout}
                  />
                </RequireAccess>
              }
            />
            <Route
              path="/sports-admin/create"
              element={
                <RequireAccess allowed={canUseSportsAdmin(user)} what="manage the sports catalog">
                  <SportsAdminCreate
                    token={token}
                  />
                </RequireAccess>
              }
            />
            <Route
              path="/sports-admin/edit/:id"
              element={
                <RequireAccess allowed={canUseSportsAdmin(user)} what="manage the sports catalog">
                  <SportsAdminEdit
                    token={token}
                  />
                </RequireAccess>
              }
            />
            <Route
              path="/organization-sports"
              element={
                <OrganizationSportsManagement
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/tournaments"
              element={
                <TournamentList
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/tournaments/create"
              element={
                <RequireAccess
                  allowed={canCreateCompetition(user)}
                  what="create tournaments"
                  backTo="/tournaments"
                >
                  <TournamentCreation
                    token={token}
                    user={user}
                  />
                </RequireAccess>
              }
            />
            <Route
              path="/tournaments/:id/edit"
              element={
                <TournamentEdit
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/tournaments/:id"
              element={
                <TournamentDetails
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/leagues"
              element={
                <Leagues
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/leagues/:id"
              element={
                <Leagues
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/leagues/:id/teams/:teamId"
              element={<LeagueTeamPage token={token} />}
            />
            <Route
              path="/matches"
              element={
                <MatchList
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/matches/create"
              element={
                <MatchCreation
                  token={token}
                  user={user}
                />
              }
            />
            <Route
              path="/matches/manage"
              element={
                <MatchManagement
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/matches/:matchId/edit"
              element={
                <MatchEdit
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/matches/:matchId"
              element={
                <MatchDetails
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/matches/:matchId/live"
              element={
                <LiveMatch
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/matches/:matchId/score"
              element={
                <BadmintonScoreTracker
                  match={null}
                  onScoreUpdate={(data) => debugLog('Score updated:', data)}
                />
              }
            />
            <Route
              path="/tournaments/:tournamentId/matches"
              element={
                <MatchList
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            {/* Team Management Routes */}
            <Route
              path="/teams/*"
              element={
                <TeamsBridge
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />

            {/* Drop-in Routes */}
            <Route
              path="/drop-in"
              element={
                <DropInList
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/drop-in/create"
              element={
                <DropInCreation
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/drop-in/:id"
              element={
                <DropInDetails
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/drop-in/:id/edit"
              element={
                <DropInEdit
                  token={token}
                  user={user}
                  onLogout={handleLogout}
                />
              }
            />
            <Route
              path="/players/:userId"
              element={
                <PlayerProfile token={token} onLogout={handleLogout} />
              }
            />
            <Route
              path="/players/:userId/followers"
              element={
                <FollowList token={token} onLogout={handleLogout} />
              }
            />
            <Route
              path="/players/:userId/following"
              element={
                <FollowList token={token} onLogout={handleLogout} />
              }
            />
            <Route
              path="/messages"
              element={
                <DirectMessages token={token} onLogout={handleLogout} />
              }
            />
            <Route
              path="/messages/:partnerId"
              element={
                <DirectMessages token={token} onLogout={handleLogout} />
              }
            />

            {/* Stats Routes */}
            <Route
              path="/stats"
              element={<StatsPage token={token} onLogout={handleLogout} />}
            />
            <Route
              path="/stats/:sportSlug"
              element={<StatsPage token={token} onLogout={handleLogout} />}
            />

            {/* My schedule: the full list (the dashboard shows the next few) */}
            <Route
              path="/schedule"
              element={<SchedulePage token={token} onLogout={handleLogout} />}
            />

            {/* Payments: get paid (Stripe Connect), sandbox test checkout, history */}
            <Route
              path="/payments"
              element={<PaymentsPage token={token} onLogout={handleLogout} />}
            />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        )}
      </Routes>
      {/* Feedback button — shown only on login (unauthenticated) and profile pages */}
      {/* Never on the venue screen (TV): no app chrome there */}
      {!routeLocation.pathname.startsWith('/screen/') &&
        (routeLocation.pathname === '/login' ||
          routeLocation.pathname === '/profile' ||
          !isAuthenticated) && <FeedbackButton />}
    </div>
  );
}

/** Preserves the attempted URL so AuthUnified can redirect back after login */
function RedirectToLogin() {
  const location = useLocation();
  return (
    <Navigate
      to="/login"
      state={{ from: location.pathname + location.search }}
      replace
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <WebSocketProvider>
          <UserFiltersProvider>
            <Router>
              <AppContent />
            </Router>
          </UserFiltersProvider>
        </WebSocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;


